/*
 * Utilities for Maps.
 */

import {Card} from "primereact/card";
import {useTranslation} from "react-i18next";

import markerHouse from "@reva/shared/assets/images/marker_haus.png";
import {selectByLanguage} from "@reva/shared/i18n";
import {MimeType} from "@reva/shared/mime-type";
import {Feature, View} from "ol";
import {GeoJSON, WMTSCapabilities} from "ol/format";
import {Circle as GeomCircle, LineString} from "ol/geom";
import Point from "ol/geom/Point";
import {Vector as VectorLayer} from "ol/layer";
import TileLayer from "ol/layer/Tile";
import {fromLonLat, get as getProjection} from "ol/proj";
import {register as registerProj4} from "ol/proj/proj4";
import OSM from "ol/source/OSM";
import TileWMSSource from "ol/source/TileWMS";
import VectorSource from "ol/source/Vector";
import WMTS, {optionsFromCapabilities} from "ol/source/WMTS";
import {getLength} from "ol/sphere";
import {Fill, Icon, Stroke, Style, Text} from "ol/style";
import proj4 from "proj4";

import {isCurrentEnvironmentInDevelopMode} from "../environment";
import {loadGeoserverTileAsync} from "../redux/geoserverThunk";
import {LAYER_COPERNICUS} from "../redux/wmtsSlice";
import {aerialImageryForMunicipality} from "./aerialImagery";
import {FALLBACK_ICON, POIS_CATEGORY_TO_ICON} from "./pois";

/** @typedef {import("ol").Map} OLMap */
/** @typedef {import("ol").View} OLView */
/** @typedef {import("ol/coordinate").Coordinate} OLCoordinate */
/** @typedef {import("ol/layer/Layer").Layer} OLLayer */
/** @typedef {import("ol/Collection").Collection} OLCollection */
/** @typedef {import("ol/format").GeoJSON} GeoJSON */
/** @typedef {import("ol-layerswitcher/dist/ol-layerswitcher").Options} LayerSwitcherOptions */
/** @typedef {import("ol/control/ScaleLine").Options} ScaleLineOptions */
/** @typedef {import("ol/interaction/Draw").Options} DrawOptions */

/**
 * @typedef OpenLayersCoords
 * @property {number} lat
 * @property {number} lon
 */

/**
 * @typedef {OpenLayersCoords} MapView
 * @property {number} [zoom]
 * @property {number} [resolution]
 * @property {boolean} [resetCenterOnChange]
 * @property {boolean} [resetCenterOnZoomChange]
 * @property {number} [resetCounter] triggers reset Center
 * @property {Extent} [extentToFit]
 * @property {boolean} [fixedZoom]
 */

/**
 * @typedef {"OSM" | "Vector" | "Geoserver" | "ExternalWms" | "GeoJSON" | "Circle"} MapLayerType
 */

/**
 * @typedef {object} MapLayer
 * @property {MapLayerType} layerType type of layer
 * @property {string} [key] key to identify the layer
 * @property {CommonLayerOptions | GeoserverLayerOptions | VectorLayerOptions} [options] additional configuration options for the layer
 */

/**
 * @typedef {object} CommonLayerOptions
 * @property {string|null} [title] title (shown in layer switcher)
 * @property {boolean|null} [visible] if set to false, hide the layer by default
 * @property {"base"|null} [type] type of layer (used in layer switcher) - "base" layers are shown switched with radio button
 * @property {string|null} [background] background color of layer
 */

/**
 * @typedef {CommonLayerOptions} GeoserverLayerOptions
 * @property {string} layer REVA layer to show
 * @property {"WMS"|"WMTS"} sourceType Geoserver Source type (WMS or WMTS)
 * @property {PersistenceAddressData|null} [address] queried REVA address, which is transmitted to AVM-API for bookkeeping
 * @property {string} [title] optional title of layer
 * @property {number} [opacity] optional opacity (between 0 and 1 - default is 1)
 * @property {string} [style] style (if not provided, default style is used)
 * @property {object} [params] additional params for geoserver
 */

/**
 * @typedef {CommonLayerOptions} ExternalWmsLayerOptions
 * @property {string} url WMS-Basis-URL (GetMap)
 * @property {string} layer WMS-Layername
 * @property {Object<string, string>} [params] zusätzliche WMS-Parameter (z. B. TIME)
 * @property {string} [attributions] Quellenvermerk (HTML)
 */

/**
 * @typedef {OpenLayersCoords} VectorLayerMarker
 * @property {string} name
 * @property {number} radius
 */

/**
 * @typedef {CommonLayerOptions} VectorLayerOptions
 * @property {OpenLayersCoords[]} [markers]
 * @property {boolean} [highlighted]
 */

/**
 * @typedef {CommonLayerOptions} GeoJSONOptions
 * @property {object} [markers]
 * @property {string} [vehicle]
 * @property {number[]} [highlightedMarkers]
 * @property {number} displacement
 */

/**
 * @typedef {MapModelEventhandlers} MapModelEventhandlers
 * @property {(object)=>void} hover_feature_event handler for hover events
 * @property {(object)=>void} fade_out_event handler for fade out events
 * @property {(object)=>void} click_event handler for click events
 */

/**
 * @typedef MapModel
 * @property {MapView} view
 * @property {MapLayer[]} layers
 * @property {MapModelLayerSwitcher|null} [switcher]
 * @property {MapModelScaleLine|null} [scaleLine]
 * @property {MapModelInteraction|null} [interaction]
 * @property {MapModelEventhandlers|null} eventHandlers
 */

/**
 * @typedef MapModelLayerSwitcher
 * @property {boolean} enabled is the layer switcher enabled/visible?
 * @property {LayerSwitcherOptions|null} [options] the options for the layer switcher
 */

/**
 * @typedef MapModelScaleLine
 * @property {boolean} enabled is the scale control enabled/visible?
 * @property {ScaleLineOptions|null} [options] the options for the scale line
 */

/**
 * @typedef MapModelInteraction
 * @property {boolean} enabled is the scale control enabled/visible?
 * @property {VectorLayer} [layer] layer used to persist drawn features
 * @property {DrawOptions|null} [options] the options for the layer switcher
 * @property {(Draw) => void} [drawFunction] functions to apply on maps draw interaction
 */

/**
 * WGS 84 / Pseudo-Mercator -- Spherical Mercator, Google Maps, OpenStreetMap, Bing, ArcGIS, ESRI
 */
export const EPSG_3857 = "EPSG:3857";
/** Proj4 Definition for EPSG:3857
 *  https://epsg.io/3857
 * */
const EPSG_3857_PROJ4 =
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +axis=neu +no_defs +type=crs";
/** Gauß-Krüger Koordinatensystem */
export const EPSG_31467 = "EPSG:31467";
/** Proj4 Definition for EPSG:31467 */
const EPSG_31467_PROJ4 =
    "+proj=tmerc +lat_0=0 +lon_0=9 +k=1 +x_0=3500000 +y_0=0 +ellps=bessel +datum=potsdam +units=m +no_defs";

export function registerCRSs() {
    proj4.defs(EPSG_31467, EPSG_31467_PROJ4);
    registerProj4(proj4);
    proj4.defs(EPSG_3857, EPSG_3857_PROJ4);
    registerProj4(proj4);

    // Invert axis orientation for EPSG 3857 (see: https://osgeo-org.atlassian.net/browse/GEOS-10902)
    //https://osgeo-org.atlassian.net/browse/GEOS-10902
    const projEPSG3857 = getProjection(EPSG_3857);
    projEPSG3857.axisOrientation_ = "neu";
}

const ATTR_KEY = "reva_key";
const ATTR_OPTIONS = "reva_options";

// Kacheln der nächst-gröberen Zoomstufe vorladen, damit beim Rauszoomen keine leere Fläche
// entsteht (OL lädt dann verzögert z-1). Bewusst nur 1 Level — jede Kachel ist ein eigener
// authentifizierter Request, höhere Werte würden viele Zusatz-Requests erzeugen.
const TILE_PRELOAD_LEVELS = 1;
// OpenLayers-Layer-Cache in ANZAHL Kacheln (nicht Bytes!). ~48 MB bei durchschnittlich ~48 KB
// pro PNG-Kachel → ~1000 Kacheln. Das Byte-Budget liegt im separaten tileCache (geoserver-tile.ts).
const TILE_LAYER_CACHE_SIZE = 1000;

// OGC standard pixel size: 0.28mm => DPI = 25.4 / 0.28
const OL_DPI = 25.4 / 0.28;
const OL_INCHES_PER_METER = 1000 / 25.4;

/**
 * Convert a map scale denominator to an OpenLayers resolution.
 * @param {number} scale scale denominator (e.g. 750 or 1000)
 * @param {number} lat latitude in degrees
 * @returns {number} OpenLayers view resolution
 */
export function resolutionFromScale(scale, lat) {
    const latRad = (lat * Math.PI) / 180;
    const latCorrection = 1 / Math.cos(latRad);
    return (scale * latCorrection) / (OL_DPI * OL_INCHES_PER_METER);
}

/**
 * Convert an OpenLayers resolution to an approximate map scale denominator.
 * @param {number} resolution OpenLayers view resolution
 * @param {number} lat latitude in degrees
 * @returns {number} scale denominator
 */
export function scaleFromResolution(resolution, lat) {
    const latRad = (lat * Math.PI) / 180;
    const latCorrection = 1 / Math.cos(latRad);
    return (resolution * OL_DPI * OL_INCHES_PER_METER) / latCorrection;
}

/**
 * Rundet einen Maßstabsnenner für die reine Anzeige auf glatte Hunderter
 * (letzte 2 Stellen -> "00"), z.B. 124666 -> 124700. Die Kartenauflösung bleibt
 * unverändert; nur der angezeigte Wert wirkt "rund" (stimmt dann nicht mehr exakt).
 * Mindestwert 100, damit nie "1 : 0" entsteht.
 * @param {number} scale scale denominator
 * @returns {number} rounded scale denominator
 */
export function roundScaleForDisplay(scale) {
    return Math.max(100, Math.round(scale / 100) * 100);
}

/**
 * Build a MapView that uses persisted zoom when available, otherwise falls back
 * to a resolution derived from the given default scale denominator.
 *
 * @param {{ lat: number, lon: number }} coords
 * @param {number} defaultScale
 * @param {MapSettings | null} persistedView  from useMapSettings
 * @param {object} [extras] additional MapView properties (minZoom, maxZoom, resetCounter, …)
 * @returns {MapView}
 */
export function createScaleView(coords, defaultScale, persistedView, extras = {}) {
    if (persistedView) {
        return {lat: persistedView.lat, lon: persistedView.lon, zoom: persistedView.zoom, ...extras};
    }
    return {lat: coords.lat, lon: coords.lon, resolution: resolutionFromScale(defaultScale, coords.lat), ...extras};
}

// Debugger flag (if set to false, suppresses all map debug outputs)
export const DEBUG_MAPS = true;

export const createLayerSwitcher = (t, startActive = false, reverse = true, groupSelectStyle = "group") => ({
    enabled: true,
    options: {
        reverse: reverse,
        startActive: startActive,
        groupSelectStyle: groupSelectStyle,
        activationMode: "click",
        tipLabel: t("general.map.selectLayers"),
        collapseTipLabel: t("general.map.collapseSwitcher"),
        collapseLabel: "-",
    },
});

/**
 * Wrapper for Map debug logs.
 */
export function logMapDebug(...args) {
    if (DEBUG_MAPS && isCurrentEnvironmentInDevelopMode()) {
        console.debug(...args);
    }
}

/**
 * If the ONLY differences between the layer's previous options ({@link ATTR_OPTIONS}) and the new
 * options are `visible` and/or `opacity`, apply them in-place via `setVisible()`/`setOpacity()` and
 * return true. This keeps the existing OpenLayers source and its tile cache alive, so toggling a
 * layer on/off (z.B. Klassen/Index bei Mikro/Makro, Gefahren-Layer bei Klimarisiken) nicht jedes
 * Mal die bereits geladenen Kacheln verwirft. Returns false if any structural option changed — then
 * the caller must recreate the layer.
 * @param {OLLayer} existingLayer layer carrying the previous options in {@link ATTR_OPTIONS}
 * @param {object} options new layer options
 * @return {boolean} true if applied in-place, false if a full recreation is required
 */
function tryInPlaceVisibilityOpacityUpdate(existingLayer, options) {
    let prev;
    try {
        prev = JSON.parse(existingLayer[ATTR_OPTIONS] ?? "null");
    } catch {
        return false;
    }
    if (!prev || typeof prev !== "object") return false;
    // Alles außer visible/opacity vergleichen — bleibt der Rest gleich, ist es ein reiner Toggle.
    const withoutVisOpacity = (o) => {
        const {visible, opacity, ...rest} = o ?? {};
        return JSON.stringify(rest);
    };
    if (withoutVisOpacity(prev) !== withoutVisOpacity(options)) return false;
    existingLayer.setVisible(options.visible !== false);
    existingLayer.setOpacity(typeof options.opacity === "number" ? options.opacity : 1);
    return true;
}

/**
 * Setzt den Canvas-Blendmodus eines Layers (z.B. "multiply"/"darken"), damit sich mehrere
 * halbtransparente Layer bei Überlappung sichtbar mischen (dunkler/kräftiger) statt sich nur
 * gegenseitig zu überdecken (Default "source-over"). Ohne blendMode passiert nichts.
 * @param {OLLayer} layer
 * @param {string} [blendMode] gültige CanvasRenderingContext2D.globalCompositeOperation
 */
function applyBlendMode(layer, blendMode) {
    if (!blendMode) return;
    layer.on("prerender", (e) => {
        if (e.context) e.context.globalCompositeOperation = blendMode;
    });
    layer.on("postrender", (e) => {
        if (e.context) e.context.globalCompositeOperation = "source-over";
    });
}

/**
 * Configurator for a layer type.
 */
class LayerConfigurator {
    /**
     * @param {MapLayerType} layerType handled layer type
     */
    constructor(layerType) {
        this.layerType = layerType;
    }

    /**
     * Create new layer of implemented type
     * @param {object} [options] layer options
     * @param {Dispatch} dispatch Redux dispatcher for API loading
     * @return {OLLayer} created layer
     */
    createLayer(options, dispatch) {
        throw new Error(`createLayer() must be implemented by ${this.layerType}`);
    }

    /**
     * Update an existing layer, which was already assigned to the map.
     * @param {OLLayer} existingLayer already existing layer
     * @param {object} options layer options
     * @param {Dispatch} dispatch Redux dispatcher for API loading
     * @return {OLLayer|null} a new created layer, or null if the change could be applied in-place
     */
    updateLayer(existingLayer, options, dispatch) {
        // Default behaviour: discard old layer, create a new one
        return this.createLayer(options, dispatch);
    }
}

class OSMLayerConfigurator extends LayerConfigurator {
    constructor() {
        super("OSM");
    }

    /**
     * @param {CommonLayerOptions} options
     * @param {Dispatch} dispatch
     */
    createLayer(options, dispatch) {
        if (!options) {
            options = {};
        }
        return new TileLayer({
            source: new OSM({
                attributions: '<a target="_blank" href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>',
            }), // native OSM source from OpenLayers,
            title: "OpenStreetMap",
            type: options.type,
            visible: options.visible !== false,
            preload: TILE_PRELOAD_LEVELS,
            cacheSize: TILE_LAYER_CACHE_SIZE,
            background: options.background,
        });
    }
}

class GeoserverLayerConfigurator extends LayerConfigurator {
    constructor() {
        super("Geoserver");
    }

    static ATTRIBUTION = '© <a target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

    /**
     * @param {GeoserverLayerOptions} options
     * @param {Dispatch} dispatch
     */
    createLayer(options, dispatch) {
        if (!options) {
            options = {};
        }
        const layer = options.layer;
        if (!layer) {
            throw new Error("Missing layer name!");
        }
        let params = {
            VERSION: "1.1.1",
            SRS: EPSG_3857,
            FORMAT: MimeType.PNG,
            FORMAT_OPTIONS: "antialiasing:on",
            TRANSPARENT: true,
            STYLES: options.style,
        };
        if (options.sourceType === "WMS") {
            params.LAYERS = layer;
        }
        if (typeof options.params === "object" && options.params) {
            params = {...params, ...options.params};
        }
        if (typeof options.address === "object") {
            const addressParams = {
                REVA_LAT: options.address.lat,
                REVA_LON: options.address.lon,
                REVA_ADDRESS: options.address.addressQuery,
            };
            params = {...params, ...addressParams};
        }
        const source = this._createWmsSource(params, dispatch);
        const tileLayer = new TileLayer({
            title: options.title,
            type: options.type,
            visible: options.visible !== false,
            opacity: typeof options.opacity === "number" ? options.opacity : undefined,
            preload: TILE_PRELOAD_LEVELS,
            cacheSize: TILE_LAYER_CACHE_SIZE,
            source: source,
            background: options.background,
        });
        applyBlendMode(tileLayer, options.blendMode);
        return tileLayer;
    }

    /** Reiner visible/opacity-Wechsel bleibt in-place (Tile-Cache erhalten), sonst neu erstellen. */
    updateLayer(existingLayer, options, dispatch) {
        if (tryInPlaceVisibilityOpacityUpdate(existingLayer, options)) {
            return null;
        }
        return this.createLayer(options, dispatch);
    }

    /**
     * Create WMS layers source.
     * @param {object} params
     * @param {Dispatch} dispatch
     */
    _createWmsSource(params, dispatch) {
        return new TileWMSSource({
            // attributions: GeoserverLayerConfigurator.ATTRIBUTION,
            attributions: params.attributions,
            url: /* relative to GS url */ "/wms",
            serverType: "geoserver",
            /* We need to override the default load method of OpenLayers WMS, because it does not support authentication out fo the box */
            tileLoadFunction: (tile, src) => dispatch(loadGeoserverTileAsync(tile, src)),
            params: params,
        });
    }
}

class GeoserverWmtsLayerConfigurator extends LayerConfigurator {
    constructor() {
        super("GeoserverWMTS");
    }

    /**
     * Create WMTS layers source.
     * @param {string} capabilities
     * @param {string} layer
     * @param {string} matrixset
     * @param {object} params
     * @param {Dispatch} dispatch
     */
    _createWmtsSource(capabilities, layer, matrixset, params, dispatch) {
        const parsedCapabilities = new WMTSCapabilities().read(capabilities);
        const fromCapabilities = optionsFromCapabilities(parsedCapabilities, {
            layer: layer,
            matrixSet: matrixset,
        });
        // Expected WMTS Request:
        // https://geoserver.value-marktdaten.de:444/geoserver/gwc/service/wmts?layer=reva%3Awohnlagen_mikro_decorated&style=&tilematrixset=WebMercatorQuad
        // &Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix=16&TileCol=40000&TileRow=23000
        fromCapabilities.tileLoadFunction = (tile, src) => dispatch(loadGeoserverTileAsync(tile, src));
        fromCapabilities.urls = ["/wmts"];
        fromCapabilities.attributions = params.attributions || GeoserverLayerConfigurator.ATTRIBUTION;
        fromCapabilities.attributionsCollapsible = false;
        // Hinweise: Geoserver vertauscht X und Y Koordinaten in den Capabilities von EPSG:3857:
        // Wurde bereits einmalig (global) in Funktion registerCRSs() eingestellt!
        return new WMTS(fromCapabilities);
    }

    /**
     * Create WMTS layers source.
     * @param options
     * @param {Dispatch} dispatch
     */
    createLayer(options, dispatch) {
        if (!options) {
            options = {};
        }
        const layer = options.layer;
        if (!layer) {
            throw new Error("Missing layer name!");
        }

        const matrixset = options.matrixset;
        if (!matrixset) {
            throw new Error(`Missing matrixset for layer ${layer}!`);
        }

        const capabilities = options.capabilities;
        if (!capabilities) {
            throw new Error(`Missing capabilities for layer:${layer}`);
        }

        logMapDebug(`Loaded Capabilities for ${layer} with matrix set ${matrixset}`);
        let params = {
            FORMAT: MimeType.PNG,
            STYLES: options.style,
        };
        if (typeof options.params === "object" && options.params) {
            params = {...params, ...options.params};
        }
        if (typeof options.address === "object") {
            const addressParams = {
                REVA_LAT: options.address.lat,
                REVA_LON: options.address.lon,
                REVA_ADDRESS: options.address.addressQuery,
            };
            params = {...params, ...addressParams};
        }
        const source = this._createWmtsSource(capabilities, layer, matrixset, params, dispatch);
        /* We need to override the default load method of OpenLayers WMS, because it does not support authentication out fo the box */
        return new TileLayer({
            title: options.title,
            type: options.type,
            visible: options.visible !== false,
            opacity: typeof options.opacity === "number" ? options.opacity : undefined,
            preload: TILE_PRELOAD_LEVELS,
            cacheSize: TILE_LAYER_CACHE_SIZE,
            source: source,
            background: options.background,
        });
    }

    /** Reiner visible/opacity-Wechsel bleibt in-place (Tile-Cache erhalten), sonst neu erstellen. */
    updateLayer(existingLayer, options, dispatch) {
        if (tryInPlaceVisibilityOpacityUpdate(existingLayer, options)) {
            return null;
        }
        return this.createLayer(options, dispatch);
    }
}

/**
 * Normalize a value into an array: arrays pass through, null/undefined become [], anything else is wrapped.
 * @param {*} value
 * @return {Array}
 */
function _toArray(value) {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    return [value];
}

class VectorLayerConfigurator extends LayerConfigurator {
    constructor() {
        super("Vector");
    }

    /**
     * @param {number} scale scale of the marker
     * @return {Style}
     */
    static markerStyle = (scale) =>
        new Style({
            image: new Icon({
                anchorXUnits: "pixels",
                anchorYUnits: "pixels",
                anchor: [10, 32],
                src: markerHouse,
                scale: scale,
            }),
        });

    /**
     * @param {VectorLayerMarker} md
     * @param {number} scale
     * @return {Feature<Point>}
     */
    static convertMarkerDataToPoint(md, scale) {
        const feature = new Feature({
            geometry: new Point(fromLonLat([md.lon, md.lat], EPSG_3857)),
            name: md.name,
        });
        feature.setStyle(VectorLayerConfigurator.markerStyle(scale));
        feature.setId("marker_point");
        return feature;
    }

    static textStyle = (md) =>
        new Style({
            text: new Text({
                text: md.name,
                font: "bold 12px Calibri,sans-serif",
                fill: new Fill({
                    color: "black",
                }),
                stroke: new Stroke({
                    color: "white",
                    width: 2,
                }),
            }),
        });

    static convertMarkerDataToText(index, md, multiplier) {
        if (!md.radius) return [];
        const center = fromLonLat([md.lon, md.lat], EPSG_3857);
        const diagonal = index % 2 === 1;
        const offset = md.radius * multiplier;
        const diagonalOffset = offset / Math.sqrt(2);

        const iconFeatures = diagonal
            ? [
                  new Feature({geometry: new Point([center[0] + diagonalOffset, center[1] - diagonalOffset])}), // NE
                  new Feature({geometry: new Point([center[0] + diagonalOffset, center[1] + diagonalOffset])}), // SE
                  new Feature({geometry: new Point([center[0] - diagonalOffset, center[1] + diagonalOffset])}), // SW
                  new Feature({geometry: new Point([center[0] - diagonalOffset, center[1] - diagonalOffset])}), // NW
              ]
            : [
                  new Feature({geometry: new Point([center[0], center[1] - offset])}), // N
                  new Feature({geometry: new Point([center[0], center[1] + offset])}), // S
                  new Feature({geometry: new Point([center[0] - offset, center[1]])}), // W
                  new Feature({geometry: new Point([center[0] + offset, center[1]])}), // E
              ];

        iconFeatures.forEach((f) => {
            f.setStyle(VectorLayerConfigurator.textStyle(md));
        });
        return iconFeatures;
    }

    /**
     * Convert markers to vector features
     * @param {VectorLayerMarker[]} md
     * @param {boolean} [highlighted]
     * @return {Feature<Point|Circle>[]}
     */
    static convertMarkerDataToFeatures(md, highlighted) {
        if (!md || md.length === 0) return [];
        const center = fromLonLat([md[0].lon, md[0].lat], EPSG_3857);
        const radius = getLength(new LineString([center, [center[0] + md[0].radius, center[1]]]));
        const meterMultiplier = md[0].radius / radius;
        const features = md.map((md) => {
            if (!md.radius) {
                return VectorLayerConfigurator.convertMarkerDataToPoint(md, highlighted === true ? 1.6 : 1.0);
            }
            return VectorLayerConfigurator.convertMarkerDataToCircle(md, meterMultiplier);
        });
        const items = md
            .flatMap((md, ind) => VectorLayerConfigurator.convertMarkerDataToText(ind, md, meterMultiplier))
            .filter((md) => md !== undefined);
        return features.concat(items);
    }

    static circleStyle = new Style({
        fill: null,
        stroke: new Stroke({color: "purple", width: 2}),
    });

    /**
     * Convert markers to circle Features
     * @param {VectorLayerMarker} md
     * @param {number} multiplier
     * @return {Feature<Circle>}
     */
    static convertMarkerDataToCircle(md, multiplier) {
        const circleFeature = new Feature({
            geometry: new GeomCircle(fromLonLat([md.lon, md.lat], EPSG_3857), md.radius * multiplier),
        });
        circleFeature.setId(`marker_circle_${md.radius}`);
        circleFeature.setStyle(VectorLayerConfigurator.circleStyle);
        return circleFeature;
    }

    /**
     * @param {VectorLayerOptions} options
     * @param {Dispatch} dispatch
     */
    createLayer(options, dispatch) {
        if (!options) {
            options = {};
        }
        const markers = options.markers;
        const highlighted = options.highlighted;
        const features = VectorLayerConfigurator.convertMarkerDataToFeatures(markers, highlighted);
        return new VectorLayer({
            source: new VectorSource({
                projection: EPSG_3857,
                features: features,
            }),
            declutter: true,
            background: options.background,
        });
    }
}

class ExternalWmsLayerConfigurator extends LayerConfigurator {
    constructor() {
        super("ExternalWms");
    }

    /**
     * Frei erreichbarer WMS (z.B. Luftbilder der Länder), direkt vom Browser geladen, ohne REVA-GeoServer.
     * @param {ExternalWmsLayerOptions} options
     * @param {Dispatch} dispatch
     */
    createLayer(options, dispatch) {
        if (!options?.url || !options.layer) {
            throw new Error("Missing url or layer name!");
        }
        return new TileLayer({
            title: options.title,
            type: options.type,
            visible: options.visible !== false,
            preload: TILE_PRELOAD_LEVELS,
            cacheSize: TILE_LAYER_CACHE_SIZE,
            background: options.background,
            source: new TileWMSSource({
                url: options.url,
                attributions: options.attributions,
                params: {
                    ...options.params,
                    LAYERS: options.layer,
                    // Immer 1.1.1: EPSG:3857 ist wegen GEOS-10902 (WMTS-Capabilities von GeoServer) per proj4 mit
                    // "+axis=neu" registriert; bei 1.3.0 vertauscht OpenLayers deshalb die BBOX-Achsen (#19684).
                    VERSION: "1.1.1",
                    FORMAT: MimeType.JPEG,
                },
                crossOrigin: "anonymous",
            }),
        });
    }

    /** Reiner visible-Wechsel bleibt in-place (Tile-Cache erhalten), sonst neu erstellen. */
    updateLayer(existingLayer, options, dispatch) {
        if (tryInPlaceVisibilityOpacityUpdate(existingLayer, options)) {
            return null;
        }
        return this.createLayer(options, dispatch);
    }
}

class GeoJSONLayerConfigurator extends LayerConfigurator {
    constructor() {
        super("GeoJSON");
    }

    /**
     * @param {GeoJSONOptions} options
     * @param {Dispatch} dispatch
     */
    createLayer(options, dispatch) {
        if (!options) {
            options = {};
        }
        //deep copy to omit mutating options
        const markers = JSON.parse(JSON.stringify(options.markers));
        const highlighted = options.highlightedMarkers
            ? options.highlightedMarkers.filter((m) => -1 < m && m < markers.features.length)
            : [];
        // Highlight the specific POI(s) that were clicked (by unique id), not their whole category.
        const highlightedUids = markers.features
            .filter((f, i) => highlighted.includes(i))
            .map((f) => f.properties.poi_uid)
            .filter((uid) => uid !== undefined && uid !== null);
        markers.features = markers.features.sort((a, b) => {
            if (highlightedUids.length === 0) {
                return 0;
            }
            //Sortiere zur Darstellung PoIs nach hinten, wenn diese gehighlighted werden sollen
            const ahighlighted = highlightedUids.includes(a.properties.poi_uid);
            const bhighlighted = highlightedUids.includes(b.properties.poi_uid);
            if (ahighlighted && !bhighlighted) {
                return 1;
            } else if (!ahighlighted && bhighlighted) {
                return -1;
            } else {
                return 0;
            }
        });
        if (options.highlightedMarkers.length > 0)
            logMapDebug(
                "Creating GeoJSON Layer with highlights:",
                options.highlightedMarkers,
                options.markers.features.map((f) => f.properties.type_en.toLowerCase()),
                highlightedUids,
            );

        const baseImage = (feature) => {
            const kategorie_en = feature.get("type_en");
            const type_en = feature.get("category_en") === "Type of Municipality" ? "municipal_center" : null;
            const featureHighlighted = highlightedUids.includes(feature.get("poi_uid"));
            const opacity = highlightedUids.length === 0 || featureHighlighted ? 1 : 0.5;
            const scale = highlightedUids.length === 0 || !featureHighlighted ? 1 : 1.5;
            const displacement = scale === 1.5 ? options.displacement || 0 : 0;
            const icon = POIS_CATEGORY_TO_ICON[type_en ?? kategorie_en.toLowerCase()];
            const src = !kategorie_en || !icon ? FALLBACK_ICON : icon;
            return new Icon({
                anchorXUnits: "pixels",
                anchorYUnits: "pixels",
                anchor: [10, 32],
                src: src,
                opacity: opacity,
                scale: scale,
                displacement: [0, displacement],
            });
        };

        const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(markers, {
                dataProjection: "EPSG:4326",
                featureProjection: EPSG_3857,
            }),
        });

        return new VectorLayer({
            source: vectorSource,
            style: (feature) => {
                const image = baseImage(feature);
                return new Style({image: image});
            },
            background: options.background,
        });
    }

    updateLayer(existingLayer, options, dispatch) {
        if (options.vehicle !== existingLayer[ATTR_OPTIONS].vehicle) return this.createLayer(options, dispatch);
        // if (options.highlightedMarkers !== existingLayer[ATTR_OPTIONS].highlightedMarkers)
        //     return this.createLayer(options, dispatch);
        const keyMapper = (os) => new Set(os.markers.map((m) => m.key + m.geometry));
        if (keyMapper(options) !== keyMapper(existingLayer[ATTR_OPTIONS])) {
            return this.createLayer(options, dispatch);
        }
        return null;
    }
}

/** @type {LayerConfigurator[]} */
const LAYER_CONFIGURATORS = [
    new OSMLayerConfigurator(),
    new GeoserverLayerConfigurator(),
    new GeoserverWmtsLayerConfigurator(),
    new ExternalWmsLayerConfigurator(),
    new VectorLayerConfigurator(),
    new GeoJSONLayerConfigurator(),
];

/** @type {Map<MapLayerType, LayerConfigurator>} */
const LAYER_CONFIGURATORS_BY_TYPE = new Map(LAYER_CONFIGURATORS.map((lc) => [lc.layerType, lc]));

/**
 * Validate a Map LayerDev, and check if required attributes are provided.
 * @param {MapLayer} layerDef
 * @return {LayerConfigurator} layer configurator if layer is valid, null if layer is invalid or has no configurator
 * @private
 */
function _findLayerConfigurator(layerDef) {
    if (!layerDef) {
        console.warn("Empty layer");
        return null;
    }
    if (!layerDef.key) {
        console.warn("Empty key in ", layerDef);
        return null;
    }
    if (!layerDef.layerType) {
        console.warn("Empty layerType in ", layerDef);
        return null;
    }
    const configurator = LAYER_CONFIGURATORS_BY_TYPE.get(layerDef.layerType);
    if (!configurator) {
        console.warn(`No Layer configurator for ${layerDef.layerType}`);
        return null;
    }
    return configurator;
}

/**
 * Create a single layer for map.
 * @param {MapLayer} layerDef
 * @param {Dispatch} dispatch Redux dispatcher for API loading
 * @return {OLLayer|null}
 * @private
 */
function _createLayer(layerDef, dispatch) {
    const configurator = _findLayerConfigurator(layerDef);
    if (!configurator) {
        return null;
    }
    /** @type {OLLayer} */
    let layer;
    try {
        layer = configurator.createLayer(layerDef.options, dispatch);
    } catch (e) {
        console.error(`Error creating layer ${JSON.stringify(layerDef)}`, e);
        return null;
    }
    if (!layer) {
        console.error(`Error creating layer ${JSON.stringify(layerDef)} (result is empty)`);
        return null;
    }
    layer[ATTR_KEY] = layerDef.key;
    layer[ATTR_OPTIONS] = JSON.stringify(layerDef.options);
    return layer;
}

/**
 * Update a single layer in the map.
 * @param {OLLayer} existingLayer
 * @param {MapLayer} layerDef
 * @param {Dispatch} dispatch Redux dispatcher for API loading
 * @return {OLLayer|null} updated layer, or null if the update was done in-place
 * @private
 */
function _updateLayer(existingLayer, layerDef, dispatch) {
    const configurator = _findLayerConfigurator(layerDef);
    if (!configurator) {
        return null;
    }
    try {
        const options = layerDef.options;
        const updatedLayer = configurator.updateLayer(existingLayer, options, dispatch);
        if (updatedLayer) {
            // new layer was created
            updatedLayer[ATTR_KEY] = layerDef.key;
            updatedLayer[ATTR_OPTIONS] = JSON.stringify(options);
        } else {
            // existing layer was updated
            existingLayer[ATTR_OPTIONS] = JSON.stringify(options);
        }
        return updatedLayer;
    } catch (e) {
        console.error(`Error updating layer ${layerDef.key}`, e);
        return null;
    }
}

/**
 * Add layers to (empty) Map.
 * @param {OLMap} map
 * @param {Dispatch} dispatch Redux dispatcher for API loading
 * @param {MapLayer[]} layers
 */
export function createLayers(map, dispatch, layers) {
    if (!Array.isArray(layers)) return;

    logMapDebug("Adding all layers to map", map);
    for (const layerDef of layers) {
        const layer = _createLayer(layerDef, dispatch);
        if (!layer) continue;
        logMapDebug(`Adding ${layerDef.layerType} layer (key=${layerDef.key})`);
        // logMapDebug(`${JSON.stringify(layerDef.options)}`, layerDef)
        map.addLayer(layer);
    }
    map[ATTR_OPTIONS] = JSON.stringify(layers);
    logMapDebug("All layers finished", map);
}

/**
 * Compare configured layer of Map.
 * If the layer configuration diverges, clear all existing layers, and create all anew.
 * @param {OLMap} map
 * @param {Dispatch} dispatch Redux dispatcher for API loading
 * @param {MapLayer[]} layers
 * @return {boolean} true, if layers changed
 */
export function updateLayers(map, dispatch, layers) {
    if (!map) return false;
    logMapDebug("Updating all layers of map", map);
    if (map[ATTR_OPTIONS] === JSON.stringify(layers)) {
        // No options differ - nothing to update
        logMapDebug("All layers are in sync.");
        return false;
    }

    const existingKeys = map.getAllLayers().map((layer) => layer[ATTR_KEY]);
    const updateKeys = layers.map((layerDef) => layerDef.key);
    if (JSON.stringify(existingKeys) !== JSON.stringify(updateKeys)) {
        // Layer keys differ - the map was updated fundamentally
        logMapDebug(`Layers are not in sync. Clearing existing ${map.getLayers().getLength()} layers ...`);
        map.getLayers().clear();
        createLayers(map, dispatch, layers);
        return true;
    }

    // Layer keys are the same, but some layers options differ.
    // Only update those layers
    logMapDebug(`Layers are not in sync. Updating layers with new options ...`);
    for (let index = 0; index < layers.length; index++) {
        const layerDef = layers[index];
        const optionsJson = JSON.stringify(layerDef.options);
        const existingLayer = map.getLayers().item(index);
        if (optionsJson === existingLayer[ATTR_OPTIONS]) {
            logMapDebug(`Layer ${layerDef.key} is unchanged`);
            continue;
        }
        logMapDebug(`Layer ${layerDef.key} needs updating:`);
        const updatedLayer = _updateLayer(existingLayer, layerDef, dispatch);
        if (!updatedLayer) {
            logMapDebug(`Layer ${layerDef.key} was updated in-place`);
        } else {
            map.removeLayer(existingLayer);
            existingLayer.dispose();
            map.getLayers().insertAt(index, updatedLayer);
            logMapDebug(`Layer ${layerDef.key} was updated and replaced in map`);
        }
    }
    map[ATTR_OPTIONS] = JSON.stringify(layers);
    return true;
}

/**
 * Format center of view options for comparison.
 * @param {MapView} viewOptions
 * @return {[number, number]}
 */
function viewCenterCmp(viewOptions) {
    return [viewOptions.lat, viewOptions.lon];
}

/**
 * Transform center of view options in EPSG:3857.
 * @param {MapView} viewOptions
 * @return {OLCoordinate}
 */
function viewCenterEpsg3857(viewOptions) {
    return fromLonLat([viewOptions.lon, viewOptions.lat], EPSG_3857);
}

/**
 * Create new OpenLayers view.
 * @param {MapView} viewOptions
 * @return {OLView}
 */
export function createView(viewOptions) {
    logMapDebug(
        `Creating view (Lat/Lon = ${viewOptions.lat}/${viewOptions.lon}, Zoom = ${viewOptions.zoom}, Resolution = ${viewOptions.resolution})`,
    );
    const viewConfig = {
        projection: EPSG_3857,
        center: viewCenterEpsg3857(viewOptions),
        minZoom: viewOptions.minZoom ? viewOptions.minZoom : Math.min(8, viewOptions.zoom ?? 8),
        maxZoom: viewOptions.maxZoom ? viewOptions.maxZoom : 20,
    };
    if (viewOptions.resolution != null) {
        viewConfig.resolution = viewOptions.resolution;
    } else {
        viewConfig.zoom = viewOptions.zoom;
    }
    const view = new View(viewConfig);
    view[ATTR_OPTIONS] = JSON.stringify(viewCenterCmp(viewOptions));
    logMapDebug("Created OpenLayers view", view);
    logMapDebug("Resolution:", view.getResolution());
    return view;
}

/**
 * Update view of OpenLayers map.
 * @param {OLMap} map
 * @param {MapView} viewOptions
 * @param {boolean} layersChanged
 */
export function updateView(map, viewOptions, layersChanged) {
    if (!map) return;
    logMapDebug(`Updating view (Lat/Lon = ${viewOptions.lat}/${viewOptions.lon}, Zoom = ${viewOptions.zoom})`);
    let zoomChanged = false;
    // TODO: Compare Zoom with attribute
    if (viewOptions.extentToFit && map.getView().calculateExtent().toString() !== viewOptions.extentToFit.toString()) {
        logMapDebug(`Found fixed extent!`);
        logMapDebug(`Updating Extent: ${map.getView().getViewStateAndExtent().extent} --> ${viewOptions.extentToFit}`);
        const extent = viewOptions.extentToFit;
        // [0, 0, 0, 0] = Ansicht bewusst stehen lassen (Klick auf PoI in der Karte). Nach einem fit
        // legt der Ausschnitt auch das Zentrum fest: Ein anschließendes setCenter auf lat/lon (bei
        // persistierter Ansicht der letzte Kartenmittelpunkt, nicht die Adresse) würde ihn verschieben,
        // sodass beim Reinzoomen weder Adresse noch PoI sichtbar sind (#19447).
        if (JSON.stringify(extent) === JSON.stringify([0, 0, 0, 0])) {
            return;
        }
        const view = map.getView();
        view.fit(extent, {size: map.getSize()});
        delete viewOptions.extentToFit;
        return;
    } else if (viewOptions.zoom != null && viewOptions.zoom !== map.getView().getZoom() && !viewOptions.extentToFit) {
        logMapDebug(`Updating Zoom: ${map.getView().getZoom()} --> ${viewOptions.zoom}`);
        map.getView().setZoom(viewOptions.zoom);
        zoomChanged = true;
    } else {
        logMapDebug("Zoom already in sync");
    }
    const centerChangeRequired =
        map.getView()[ATTR_OPTIONS] !== JSON.stringify(viewCenterCmp(viewOptions)) ||
        (viewOptions.resetCenterOnZoomChange && zoomChanged) ||
        (viewOptions.resetCenterOnChange && (layersChanged || zoomChanged));
    if (centerChangeRequired) {
        logMapDebug(
            `Updating Center: ${map.getView()[ATTR_OPTIONS]} --> ${JSON.stringify(viewCenterCmp(viewOptions))}`,
        );
        map.getView().setCenter(viewCenterEpsg3857(viewOptions));
    } else if (viewOptions.resetCounter) {
        logMapDebug(`Reset center counter changed. Counter = ${viewOptions.resetCounter})`);
        map.getView().setCenter(viewCenterEpsg3857(viewOptions));
    } else {
        logMapDebug("Center already in sync");
    }
}

export class LayerFactory {
    /**
     * Create default OSM layer.
     * @param {string} [key] optional key
     * @param {CommonLayerOptions} [commonOptions] common options
     * @return {MapLayer}
     */
    static createOSMLayer(key, commonOptions) {
        return {
            layerType: "OSM",
            key: key || "osm",
            options: commonOptions,
        };
    }

    /**
     * Create Vector layer with marker(s).
     * @param {VectorLayerMarker|VectorLayerMarker[]|GeoRefAddress|GeoRefAddress[]} markers one or multiple markers and/or addresses
     * @param {RevaLanguage} language UI language, decides which display name is used
     * @param {string} [key] optional key
     * @param {boolean} [highlighted] optional flag if markers should be highlighted
     * @return {MapLayer}
     */
    static createMarkerLayer(markers, language, key, highlighted) {
        markers = _toArray(markers);
        /** @type {VectorLayerMarker[]} */
        const convertedMarkers = markers
            .map((marker) => {
                if (marker.lat && marker.lon) {
                    if (marker.displayNameEN && marker.displayNameDE) {
                        // Is GeoRef Address
                        return {
                            lat: marker.lat,
                            lon: marker.lon,
                            name: selectByLanguage(language, marker.displayNameDE, marker.displayNameEN),
                        };
                    }
                    // Valid marker object
                    return marker;
                }
                console.warn(`Given marker is not valid: ${JSON.stringify(marker)}`);
                return null;
            })
            .filter((marker) => !!marker);
        return {
            layerType: "Vector",
            key: key || "markers",
            options: {
                markers: convertedMarkers,
                highlighted: highlighted,
            },
        };
    }

    /**
     * Create Circle layer with marker(s).
     * @param {VectorLayerMarker|VectorLayerMarker[]|GeoRefAddress|GeoRefAddress[]} markers one or multiple markers and/or addresses
     * @param {number[]} radius array of radius values
     * @param {string} [key] optional key
     * @return {MapLayer}
     */
    static createCircleLayer(markers, radius, key) {
        markers = _toArray(markers);
        radius = _toArray(radius);
        const convertedMarkers = markers
            .flatMap((marker) => {
                return radius.map((r) => {
                    if (marker.lat && marker.lon) {
                        // Is GeoRef Address
                        return {
                            lat: marker.lat,
                            lon: marker.lon,
                            name: r < 1000 ? `${r}m` : `${r / 1000}km`,
                            radius: r,
                        };
                    }
                    console.warn(`Given marker is not valid: ${JSON.stringify(marker)}`);
                    return null;
                });
            })
            .filter((marker) => !!marker);
        return {
            layerType: "Vector",
            key: key || "circle",
            options: {
                markers: convertedMarkers,
                highlighted: 0,
            },
        };
    }

    /**
     * Create GeoJSON layer with PoIs as marker(s).
     * @param {string|null} vehicle optional key
     * @param {Object} pois one or multiple markers and/or addresses
     * @param {string} [key] optional key
     * @param {number[]} [highlightedMarkerIndexes] optional array for highlighted PoIs
     * @param {number} displacement optional timestamp when POIs were highlighted
     * @return {MapLayer}
     */
    static createPoisLayer(vehicle, pois, highlightedMarkerIndexes, key, displacement) {
        return {
            layerType: "GeoJSON",
            key: key || "pois",
            options: {
                vehicle: vehicle,
                markers: pois,
                highlightedMarkers: highlightedMarkerIndexes,
                displacement: displacement,
            },
        };
    }

    /**
     * Create geoserver WMS layer.
     * @param {string} layer Geoserver layer to use
     * @param {PersistenceAddressData|null} address queried REVA address, which is transmitted to AVM-API for bookkeeping
     * @param {string|undefined} [style] optional style in geoserver
     * @param {string} [key] optional key
     * @param {number|undefined} [opacity] optional opacity
     * @param {CommonLayerOptions} [commonOptions] common options
     * @return {MapLayer}
     */
    static createGeoserverWmsLayer(layer, address, style, key, opacity, commonOptions) {
        return LayerFactory.createGeoserverLayer(layer, "WMS", address, style, key, opacity, commonOptions);
    }

    /**
     * Create geoserver WMTS layer.
     * @param {string} layer Geoserver layer to use
     * @param {PersistenceAddressData|null} address queried REVA address, which is transmitted to AVM-API for bookkeeping
     * @param {string|undefined} [style] optional style in geoserver
     * @param {string} [key] optional key
     * @param {number|undefined} [opacity] optional opacity
     * @param {CommonLayerOptions} [commonOptions] common options
     * @return {MapLayer}
     */
    static createGeoserverWmtsLayer(layer, address, style, key, opacity, commonOptions) {
        return {
            layerType: "GeoserverWMTS",
            key: key || `geoserver_wmts_${layer}`,
            options: {
                address: address,
                style: style || undefined,
                layer: layer,
                sourceType: "WMTS",
                opacity: opacity || undefined,
                ...(commonOptions || {}),
            },
        };
    }

    /**
     * Create base layer with the official aerial imagery (DOP) of the federal state of the address (#19684).
     * The layer is loaded directly by the browser from the state's WMS (layer type "ExternalWms").
     * @param {string|number|null|undefined} municipalityCode municipality code (AGS) of the address
     * @param {string} key key to identify the layer
     * @return {MapLayer|null} the layer, or null if no aerial imagery is available for the federal state
     */
    static createAerialImageryLayer(municipalityCode, key) {
        const source = aerialImageryForMunicipality(municipalityCode);
        if (!source) return null;
        const {url, layer, params, attributions} = source;
        return {layerType: "ExternalWms", key: key, options: {url, layer, params, attributions, ...LUFTBILD_OPTIONS}};
    }

    /**
     * Create geoserver layer.
     * @param {string} layer Geoserver layer to use
     * @param {"WMS"|"WMTS"} sourceType Geoserver source type (WMS or WMTS)
     * @param {PersistenceAddressData|null} address queried REVA address, which is transmitted to AVM-API for bookkeeping
     * @param {string|undefined} [style] optional style in geoserver
     * @param {string} [key] optional key
     * @param {number|undefined} [opacity] optional opacity
     * @param {CommonLayerOptions} [commonOptions] common options
     * @return {MapLayer}
     */
    static createGeoserverLayer(layer, sourceType, address, style, key, opacity, commonOptions) {
        return {
            layerType: "Geoserver",
            key: key || `geoserver_${layer}`,
            options: {
                address: address,
                style: style || undefined,
                layer: layer,
                sourceType: sourceType,
                opacity: opacity || undefined,
                ...(commonOptions || {}),
            },
        };
    }
}

/** Im Layer-Switcher gibt es immer nur die eine, anfangs ausgeblendete Basisebene "Luftbild" (#19684). */
const LUFTBILD_OPTIONS = {title: "Luftbild", type: "base", visible: false};

/**
 * Einzige Satelliten-/Luftbild-Ebene "Luftbild": das Landesluftbild des Bundeslands der Adresse,
 * sonst die ESA-Ebene über den REVA-GeoServer als Fallback (#19684).
 * @param {string|number|null|undefined} municipalityCode Gemeindeschlüssel der Adresse
 * @param {PersistenceAddressData|null} address REVA-Adresse, geht beim ESA-Fallback zur Abrechnung an den GeoServer
 * @param {string} key Layer-Key
 * @return {MapLayer}
 */
export function createLuftbildLayer(municipalityCode, address, key) {
    return (
        LayerFactory.createAerialImageryLayer(municipalityCode, key) ??
        LayerFactory.createGeoserverLayer(LAYER_COPERNICUS, "WMS", address, undefined, key, undefined, LUFTBILD_OPTIONS)
    );
}

export function MicroMacroLocationIndex() {
    return (
        <div className="OpenLayersLegendPanel">
            <div className="micromacro-location-index-scale-container">
                <div className="micromacro-location-index-scale"></div>
                <div className="micromacro-location-scale-labels">
                    <div className="micromacro-location-scale-label">0</div>
                    <div className="micromacro-location-scale-label">20</div>
                    <div className="micromacro-location-scale-label">40</div>
                    <div className="micromacro-location-scale-label">60</div>
                    <div className="micromacro-location-scale-label">80</div>
                    <div className="micromacro-location-scale-label">100</div>
                </div>
            </div>
        </div>
    );
}

function LocationClasses({class2Label, class1Label}) {
    const {t} = useTranslation();
    return (
        <div className="OpenLayersLegendPanel">
            <p className="micromacro-location-classes">
                <span className="micromacro-location-class micromacro-location-class-5"></span>
                {t("location.classVeryGood")} (81-100)
                <br />
                <span className="micromacro-location-class micromacro-location-class-4"></span>
                {t("location.classGood")} (61-80)
                <br />
                <span className="micromacro-location-class micromacro-location-class-3"></span>
                {t("location.classAverage")} (41-60)
                <br />
                <span className="micromacro-location-class micromacro-location-class-2"></span>
                {class2Label} (21-40)
                <br />
                <span className="micromacro-location-class micromacro-location-class-1"></span>
                {class1Label} (0-20)
            </p>
        </div>
    );
}

// #19261: Makrolage-Klassen umbenannt (Mäßige -> Einfache, Einfache -> Sehr einfache); Mikrolage bewusst unverändert.
export function MacroLocationClasses() {
    const {t} = useTranslation();
    return (
        <LocationClasses
            class2Label={t("location.macro.classSimple")}
            class1Label={t("location.macro.classVerySimple")}
        />
    );
}

export function MicroLocationClasses() {
    const {t} = useTranslation();
    return (
        <LocationClasses
            class2Label={t("location.micro.classModerate")}
            class1Label={t("location.micro.classSimple")}
        />
    );
}

export function MicroMacroNoteCard({specificNote}) {
    const {t} = useTranslation();
    return (
        <Card className="LandRegisterCard MapNoteCard bg-surface-100 dark:bg-surface-700 mb-6 mt-0">
            <h3 className="mt-0 mb-2">{t("general.map.note")}</h3>
            <p className="mt-0 mb-2">{specificNote}</p>
            <p className="mt-0 mb-2">{t("location.noteSharedP1")}</p>
            <p className="mt-0 mb-0">{t("location.noteSharedP2")}</p>
        </Card>
    );
}
